import React, { useEffect, useState } from "react";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import i18n from "utils/i18n";
import _ from "lodash";
import GraphqlBuilder from "utils/request/graphql/builder";
import { listArrow } from "styles/list";
import { AppRoutes } from "navigations/app-routes";
import QueryManagementBuildings from "graphql/queries/management/QueryManagementBuildings";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import LoadingComponent from "components/atoms/Loading";
import ManagementGroupedThemeList from "components/management/molecules/ManagementGroupedThemeList";
import { constantsActive } from "utils/constants/constants";
import Translate from "utils/translate";
import ManagementHeader from 'components/management/molecules/ManagementHeader'
import ThemedBlock from 'components/atoms/ThemedBlock'
import DraggableFlatList from 'react-native-draggable-flatlist'
import { View } from 'react-native'
import ThemedDivider from 'components/atoms/ThemedDivider'
import MutationManagementBuildingPositions from 'graphql/mutations/management/MutationManagementBuildingPositions'
import ObjectBuilding from 'utils/objects/building'
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import HelperString from 'utils/helper-string'
import { useShowHamburger } from 'utils/helper-device'
import { layoutStyles } from 'styles/layout'
import ListItem from 'components/atoms/ListItem'

const ManagementBuildingsIndexScene = () => {
	
	const isFocused = useIsFocused();
	const navigation = useNavigation();
	const showHamburger = useShowHamburger();
	
	const [ loading, setLoading ] = useState( true )
	const [ draggable, setDraggable ] = useState( false )
	const [ savingDraggable, setSavingDraggable ] = useState( false )
	const [ buildings, setBuildings ] = useState( [] )
	const [ changedBuildings, setChangedBuildings ] = useState( [] )
	const [ search, setSearch ] = useState( '' )
	
	const fetchBuildings = () => {
		new GraphqlBuilder()
			.addQuery( 'query', QueryManagementBuildings, {} )
			.setResultPath( ['query', 'buildings'], [] )
			.setOnSuccess( result => {
				const buildings = ObjectBuilding.order( result );
				
				setLoading( false )
				setBuildings( buildings )
			})
			.setOnError( setLoading.bind( this, false ) )
			.execute();
	}
	
	const searchChanged = ( value ) => {
		setSearch( value?.toLowerCase() ?? '' )
	}
	
	const renderItem = ( { item, index } ) => {
		return (
			<ListItem
				key={ HelperString.hash('building-render-item', item ) }
				title={ Translate.field( item, 'name' ) }
				title_add_active_tag={ true }
				item={ item }
				accessoryRight={ listArrow }
				onPress={ openBuilding }
			/>
		)
	}
	
	const renderDraggableItem = ( { item, index, drag, isActive } ) => {
		return (
			<ListItem
				title={ Translate.field( item, 'name' ) }
				title_add_active_tag={ true }
				item={ item }
				drag={ drag }
				dragIsActive={ isActive }
			/>
		)
	}
	
	const toggleDraggable = () => {
		setDraggable( !draggable )
	}
	
	const createBuilding = () => {
		openBuilding( {} );
	}
	
	const openBuilding = ( item= {} ) => {
		navigation.navigate( AppRoutes.MANAGEMENT_BUILDINGS_BUILDINGS_VIEW, {
			buildingGuid: item.guid
		});
	}
	
	const mutationBuildingPositions = () => {
		const variables = {};
		variables['building_positions'] = _.map( changedBuildings, (building, key) => {
			return (
				{
					building_guid: building.guid,
					position: key
				}
			);
		})
		
		new GraphqlBuilder()
			.addQuery( 'mutation_building_position', MutationManagementBuildingPositions, variables )
			.setOnStart( setSavingDraggable.bind( this, true ) )
			.setOnError( setSavingDraggable.bind( this, false ) )
			.setOnSuccess( result => {
				const newBuildings = ObjectBuilding.order(
					_.map( buildings, (item) => {
						return _.merge( item, _.find( result.mutation_building_position.building_positions, { 'guid': item.guid } ) );
					})
				);
				
				setSavingDraggable( false )
				setDraggable( false )
				setChangedBuildings( [] )
				setBuildings( newBuildings )
			})
			.execute()
	}

	useEffect( () => {
		fetchBuildings()
	}, [ isFocused ] )

	const filters = _.size( search ) > 2 ? [ {field: 'name', kind: 'contains', value: search } ] : null;
	
	const header = (
		<ManagementHeader
			inputs={[
				{
					placeholder: i18n.t( 'management:pages.buildings.buildings.input_search'),
					onChangeText: _.debounce( searchChanged.bind( this ), 400 )
				}
			]}
			dragButton={ HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.BUILDINGS ) ? {
				onPress: toggleDraggable.bind( this ),
				drag: draggable,
				save: mutationBuildingPositions.bind( this ),
				isSaving: savingDraggable
			} : null}
			addButton={ HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.BUILDINGS ) ? { onPress: createBuilding.bind( this ) } : null}
		/>
	);

	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				title={i18n.t('management:pages.buildings.buildings.title')}
				topNavigation={true}
				topNavigationProps={{
					showHamburgerDrawer: showHamburger
				}}>

				<Choose>
					<When condition={ loading }>
						<LoadingComponent />
					</When>
					<When condition={ draggable }>
						<ThemedBlock
							header={ header }
							shadow>
							
							<View style={ layoutStyles.flexOne }>
								<DraggableFlatList
									data={ _.filter( !_.isEmpty( changedBuildings ) ? changedBuildings : buildings, { active: constantsActive.YES } ) }
									renderItem={ renderDraggableItem.bind( this ) }
									ItemSeparatorComponent={ ThemedDivider }
									keyExtractor={ (item, index) => {
										return HelperString.hash(`draggable-item`, item.guid);
									} }
									onDragEnd={ ({ data }) => {
										return setChangedBuildings(data);
									} }
								/>
							</View>
						</ThemedBlock>
					</When>
					<Otherwise>
						<ManagementGroupedThemeList
							header={ header }
							items={ buildings }
							renderItem={ renderItem.bind( this ) }
							groupByKey={ 'active' }
							filters={ filters }
							groupByValues={ [
								constantsActive.YES,
								constantsActive.NO
							] }
						/>
					</Otherwise>
				</Choose>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

export default ManagementBuildingsIndexScene
